// @/utils/learning/backgroundFlow.js
// 模块级单例：生命周期 = 页面生命周期，与弹窗组件无关。
// 组件销毁不影响流；组件重开时通过 getFlow 恢复现场。

import md from '@/utils/markdown/md';
import { handleMathData as rawHandleMathData } from '@/api/fetch/mathService';
import { cancelStream } from './streamResume';

// 后端忙碌/排队占位文案（从 AdvancedLearning.vue 迁入）
export const BUSY_TEXT_RE = /正在全力运转|很快就会?完成/;

// ---------- 状态容器 ----------
// key = `${kgid}|${level}`，同一时间一个图谱只保留一个活跃流
// value = {
//   type: 'node' | 'diag',
//   aiContent, controller,     // 流载体，后台继续跑
//   lastHtml,                  // 最近一次渲染结果（重开时先显示）
//   done, failed, errText,     // 终态
//   busy, emptyResult,         // 节点流专属终态分支
//   finalMsg,                  // 诊断流正常完成的最终文本
//   payload,                   // 诊断流提交的 payload（重开后可重建诊断链路）
//   pendingDiagDone,           // 诊断完成但收尾未做（等用户回来）
//   onDone,                    // 组件级收尾钩子（在场时才调）
// }
const flow = { tasks: {} };

// ---------- 订阅机制 ----------
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  listeners.forEach(fn => { try { fn(); } catch (e) { console.warn(e); } });
}

// ---------- 基础 API ----------
export function getFlowKey(kgid, level) { return `${kgid}|${level}`; }
export function getFlow(key) { return flow.tasks[key] || null; }

export function updateFlow(key, patch) {
  const t = flow.tasks[key];
  if (t) {
      Object.assign(t, patch);
      t.updatedAt = Date.now(); // ★ 新增：记录最近活动时间，供"恢复最新图谱"排序
      notify();
  }
}

// 终止并清除任务（abort 后下一次轮询发现 task 没了会自动停）
export function stopFlow(key, reason) {
  const t = flow.tasks[key];
  if (t) {
      if (t.lastHtml) snapshotFlow(key);
      // ★ 只有未终态的流才需要通知后端终止；
      //   done/failed 的旧流后端缓存让其自然 TTL 过期，不动
      if (t.requestId && !t.done && !t.failed) cancelStream(t.requestId);
      if (t.controller) { try { t.controller.abort(); } catch (e) {} t.controller = null; }
      if (t._timer) clearTimeout(t._timer);
      delete flow.tasks[key];
  }
}

// 清理组件级钩子（组件销毁前调用，防止闭包持有已销毁组件）
export function detachFlowHooks(key) {
  const t = flow.tasks[key];
  if (t) t.onDone = null;
}

// ---------- 节点内容流 ----------
// config = { aiContent, controller, onDone(finalMsg) }
export function startNodeFlow(flowKey, config) {
  stopFlow(flowKey, '发起新一轮生成');
  const { aiContent, controller, onDone } = config;
  const task = {
    type: 'node',
    aiContent, controller, onDone,
    requestId: aiContent.id || '',          // ★ 续传凭证（后端缓存 key）
    receivedCount: 0,                       // ★ offset 基准（已收完整 JSON 分片数）
    lastHtml: '', done: false, failed: false,
    errText: '', busy: false, emptyResult: false,
    _lastLen: -1, updatedAt: Date.now(),
  };
  flow.tasks[flowKey] = task;

  const waitDone = () => {
    const t = flow.tasks[flowKey];
    if (!t) return;

    // ── 增量渲染 ──
    const msg = getFlowMsg(t.aiContent);
    if (typeof msg === 'string' && msg && msg !== 'loading' && msg.length !== t._lastLen) {
      t._lastLen = msg.length;
      t.lastHtml = md.render(msg);
      t.updatedAt = Date.now();
      // ★ 同步 offset：与内容增长同事件源，保证 receivedCount 与 lastHtml 一致
      t.receivedCount = t.aiContent._receivedCount || t.receivedCount || 0;
      scheduleSnapshot(flowKey);
      notify();
    }

    // ── 终态判定 ──
    if (t.aiContent.isDone === 1) {
      const finalMsg = getFlowMsg(t.aiContent);
      t.done = true;
      if (!finalMsg.trim()) {
        t.emptyResult = true;
      } else if (BUSY_TEXT_RE.test(finalMsg)) {
        t.busy = true;
        t.lastHtml = '';
      } else {
        t.lastHtml = md.render(finalMsg);
      }
      markFlowUnseen(flowKey);
      notify();
      if (t.onDone) t.onDone(finalMsg);
      return;
    }

    // ★★★ 新增：网络异常（isDone === 3）不判死，标记 paused 等续传 ★★★
    // 后端生成 task 与缓存仍在，断网只是本地连接死了。
    // 恢复时机交给外部（visibilitychange / online 事件 → resumeFromBackend）。
    if (t.aiContent.isDone === 3 && t.requestId) {
      t.paused = true;                 // 非 done 非 failed → 恢复入口不再被挡
      t._timer = null;                 // 停止本地轮询（内容源已断，轮询无意义）
      notify();                        // 视图收到 paused（见下：syncFlowView 补分支）
      return;
    }

    if (t.aiContent.isDone && t.aiContent.isDone !== 0) {
      t.failed = true;
      t.errText = t.aiContent.isDone === 2 ? '回答已被终止'
        : t.aiContent.isDone === 3 ? '网络异常' : '服务器异常';
      markFlowUnseen(flowKey);
      notify();
      if (t.onDone) t.onDone(getFlowMsg(t.aiContent));
      return;
    }

    t._timer = setTimeout(waitDone, 300);
  };
  waitDone();

  // ★ 外部恢复入口：移动端冻结回来后 setTimeout 可能被 OS 吞掉，由 resumePolling 拉起。
  //   幂等：有 _timer 或已终态则忽略；waitDone 首行校验 task 存在，重复调用安全。
  task.wake = () => {
    const t = flow.tasks[flowKey];
    if (!t || t.done || t.failed || t._timer) return;
    waitDone();
  };
  return task;
}


// ---------- 诊断流 ----------
// config = { aiContent, controller, payload, onDone(finalMsg|null, aiContent, errText|null) }
export function startDiagFlow(flowKey, config) {
  stopFlow(flowKey, '发起新一轮诊断');
  const { aiContent, controller, payload, onDone } = config;
  const task = {
    type: 'diag',
    aiContent, controller, payload, onDone,
    requestId: aiContent.id || '',          // ★
    receivedCount: 0,                       // ★
    lastHtml: '', done: false, failed: false,
    errText: '', finalMsg: '', _lastLen: -1, updatedAt: Date.now(),
  };
  flow.tasks[flowKey] = task;

  const renderLoop = () => {
    const t = flow.tasks[flowKey];
    if (!t) return;

    const msg = getFlowMsg(t.aiContent);
    if (typeof msg === 'string' && msg !== 'loading' && msg.length !== t._lastLen) {
      t._lastLen = msg.length;
      t.lastHtml = md.render(msg);
      t.updatedAt = Date.now();
      // ★ 同步 offset
      t.receivedCount = t.aiContent._receivedCount || t.receivedCount || 0;
      scheduleSnapshot(flowKey);
      notify();
    }

    // ★ 网络异常：不判死，标记 paused 等续传（同 startNodeFlow）
    if (t.aiContent.isDone === 3 && t.requestId) {
      t.paused = true;
      t._timer = null;
      notify();
      return;
    }

    if (t.aiContent.isDone && t.aiContent.isDone !== 0) {
      const finalMsg = getFlowMsg(t.aiContent);
      if (t.aiContent.isDone === 1 && !BUSY_TEXT_RE.test(finalMsg) && finalMsg.trim()) {
        t.done = true;
        t.finalMsg = finalMsg;
        t.lastHtml = md.render(finalMsg);
        markFlowUnseen(flowKey);
        notify();
        if (t.onDone) t.onDone(finalMsg, t.aiContent, null);
      } else if (t.aiContent.isDone === 1 && BUSY_TEXT_RE.test(finalMsg)) {
        t.failed = true;
        t.errText = 'BUSY';
        t.lastHtml = '';
        notify();
      } else if (t.aiContent.isDone === 1 && !finalMsg.trim()) {
        t.failed = true;
        t.errText = 'EMPTY';
        notify();
      } else {
        t.failed = true;
        t.errText = t.aiContent.isDone === 2 ? '回答已被终止'
          : t.aiContent.isDone === 3 ? '网络异常' : '服务器异常';
        notify();
        if (t.onDone) t.onDone(null, t.aiContent, t.errText);
      }
      return;
    }
    t._timer = setTimeout(renderLoop, 300);
  };
  renderLoop();

  // ★ 外部恢复入口（同 startNodeFlow.wake）
  task.wake = () => {
    const t = flow.tasks[flowKey];
    if (!t || t.done || t.failed || t._timer) return;
    renderLoop();
  };
  return task;
}


// ★ 正文 = 所有 part 的 msg 拼接
// handleMathData 会在试题/plotly 处切分新 part，文本可能分布在多个 part 里，
// 只读 parts[0] 会漏掉后续内容（卡「加载中」的根因）
function getFlowMsg(aiContent) {
    if (!aiContent) return '';
    if (Array.isArray(aiContent.parts) && aiContent.parts.length) {
      const joined = aiContent.parts
        .map(p => (p && typeof p.msg === 'string') ? p.msg : '')
        .join('');
      if (joined) return joined;
    }
    return typeof aiContent.msg === 'string' ? aiContent.msg : '';
  }

  // ---------- ★ 生成完成红点（未读完成状态）----------
// 主窗口「带着地图学习」入口红点：
// 流结束即标记未读（sessionStorage 持久化，页面刷新不丢），
// 用户打开弹窗（AdvancedLearning mounted）后清除。
const UNSEEN_KEY = 'kg_flow_unseen';

function readUnseen() {
  try { return JSON.parse(sessionStorage.getItem(UNSEEN_KEY) || '[]'); }
  catch (e) { return []; }
}
function writeUnseen(list) {
  try { sessionStorage.setItem(UNSEEN_KEY, JSON.stringify(list)); } catch (e) {}
}

// 流结束时由 service 内部调用：标记该图谱有「已完成未读」的生成
function markFlowUnseen(flowKey) {
  const list = readUnseen();
  if (!list.includes(flowKey)) {
      list.push(flowKey);
      writeUnseen(list);
  }
  // ★ 新增：终态也算一次"最近活动"，保证快照排序准确
  const t = flow.tasks[flowKey];
  if (t) t.updatedAt = Date.now();
  notify(); // 主窗口订阅者立即收到 → 红点亮起
}

// 主窗口查询红点状态：
//   1) 存在「已完成未读」的流（sessionStorage 持久化，刷新不丢）
//   2) 存在仍在后台生成的内存流（页面未刷新时，生成期间红点常亮提醒）
//   2) 如不想要"生成中提醒"，删掉下面的 some 分支即可
export function hasUnseenFlows() {
  if (readUnseen().length > 0) return true;
  return Object.values(flow.tasks).some(t => t && !t.done && !t.failed);
}

// ★ 新增：查询某个流是否处于「已完成未读」状态
// AdvancedLearning mounted 扫描恢复时用：优先恢复红点对应的那条流
export function isFlowUnseen(flowKey) {
  return readUnseen().includes(flowKey);
}

// 用户打开弹窗后调用：清除全部未读标记（红点熄灭）
export function clearUnseenFlows() {
  if (readUnseen().length) {
    writeUnseen([]);
    notify(); // 通知主窗口订阅者刷新
  }
}

// ---------- ★ 流快照持久化（解决生产环境整页刷新后内存任务丢失） ----------
// sessionStorage 保存各流的终态元数据 + lastHtml，重开后可恢复"已完成/失败"的现场。
// 进行中的流刷新后无法续跑（请求中断），只恢复其快照内容供查看。
const SNAPSHOT_KEY = 'kg_flow_snapshot';
const SNAPSHOT_MAX_AGE = 24 * 60 * 60 * 1000; // 24h 过期

function readSnapshots() {
  try {
    const all = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || '{}');
    const now = Date.now();
    let dirty = false;
    for (const k of Object.keys(all)) {
      if (!all[k] || now - all[k].savedAt > SNAPSHOT_MAX_AGE) {
        delete all[k];
        dirty = true;
      }
    }
    if (dirty) writeSnapshots(all);
    return all;
  } catch (e) { return {}; }
}

function writeSnapshots(all) {
  try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(all)); } catch (e) {}
}

// ★ 快照某个流（仅终态或有内容时才有意义）
function snapshotFlow(key) {
  const t = flow.tasks[key];
  if (!t) return;
  if (!t.done && !t.failed && !t.lastHtml) return;

  // ★ 落盘前最后同步一次 offset，保证快照里 receivedCount 最新
  if (t.aiContent) {
    t.receivedCount = t.aiContent._receivedCount || t.receivedCount || 0;
  }

  const all = readSnapshots();
  all[key] = {
    type: t.type,
    requestId: t.requestId || '',           // ★ 续传凭证随快照持久化
    receivedCount: t.receivedCount || 0,    // ★ 快照 offset
    done: !!t.done, failed: !!t.failed,
    errText: t.errText || '',
    busy: !!t.busy, emptyResult: !!t.emptyResult,
    lastHtml: t.lastHtml || '',
    finalMsg: t.finalMsg || '',
    updatedAt: t.updatedAt || Date.now(),
    savedAt: Date.now(),
  };
  writeSnapshots(all);
}


// ★ 从快照恢复为"只读流"对象（无活的请求，仅供视图渲染）
export function getFlowOrSnapshot(key) {
  const t = getFlow(key);
  if (t) return t;
  const snap = readSnapshots()[key];
  if (!snap) return null;
  return { ...snap, snapshotOnly: true };
}

// ★ 节流快照：流式过程中调用，2 秒最多落盘一次
let _snapTimer = null;
function scheduleSnapshot(key) {
  if (_snapTimer) return;
  _snapTimer = setTimeout(() => {
    _snapTimer = null;
    // 把当前所有有内容的流都刷一遍（开销小，sessionStorage 写入 ~几十 KB 级）
    for (const k of Object.keys(flow.tasks)) snapshotFlow(k);
  }, 2000);
}

// ---------- ★ 断线假死检测 / 恢复轮询 / 强制落盘 / 快照复活 ----------

/** 流是否假死：轮询在跑但内容 6 秒无增长（正常流式间隔远小于 6s） */
export function isFlowStale(key) {
  const t = flow.tasks[key];
  if (!t || t.done || t.failed) return false;
  // 优先看 SSE 心跳（后端 5s 一帧，15s 无心跳 = 连接/后端真死了）
  const hb = t.aiContent && t.aiContent._lastHb;
  if (hb) return Date.now() - hb >= 15000;
  // 无心跳信息（旧后端）→ 退回内容增长判定，阈值放宽到 30s
  return Date.now() - (t.updatedAt || 0) >= 30000;
}

/** 恢复轮询循环（冻结期间 setTimeout 被暂停时，回来后拉起） */
export function resumePolling(key) {
  const t = flow.tasks[key];
  if (t && !t.done && !t.failed && typeof t.wake === 'function') t.wake();
}

/** 强制落盘所有有内容的流（visibilitychange hidden / pagehide 时调用）。
 *  只落盘，不 abort、不 cancel —— 后端生成 task 独立于连接继续跑。 */
export function forceSnapshotAll() {
  for (const k of Object.keys(flow.tasks)) {
    snapshotFlow(k);
  }
}

/**
 * 将快照"半复活"为内存 task，承接 /resume-stream 续传。
 * 用于：切后台 → 整页刷新 → 回来（内存任务已丢，只有 sessionStorage 快照）。
 * 后端缓存 10 分钟内有效时，续传可拿回剩余内容；404 则保留快照现场。
 */
export function reviveFlowTask(key, task) {
  // 通用唤醒循环（复活 task 没有原 waitDone/renderLoop 闭包，用通用检查代替）
  task.wake = () => {
    const t = flow.tasks[key];
    if (!t || t.done || t.failed || t._timer) return;
    const check = () => {
      const cur = flow.tasks[key];
      if (!cur || cur.done || cur.failed) return;
      const msg = getFlowMsg(cur.aiContent);
      if (typeof msg === 'string' && msg && msg !== 'loading' && msg.length !== cur._lastLen) {
        cur._lastLen = msg.length;
        cur.lastHtml = md.render(msg);
        cur.updatedAt = Date.now();
        cur.receivedCount = cur.aiContent._receivedCount || cur.receivedCount || 0;
        scheduleSnapshot(key);
        notify();
      }
      if (cur.aiContent.isDone === 3 && cur.requestId) {
        cur.paused = true;
        cur._timer = null;
        notify();
        return;
      }
      if (cur.aiContent.isDone && cur.aiContent.isDone !== 0) {
        cur.done = cur.aiContent.isDone === 1;
        cur.failed = !cur.done;
        if (cur.done) {
          cur.finalMsg = msg;
          cur.lastHtml = md.render(msg);
          markFlowUnseen(key);
        }
        notify();
        return;
      }
      cur._timer = setTimeout(check, 300);
    };
    check();
  };
  flow.tasks[key] = task;
}
